import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import type {
  CheckoutEventsData,
  CheckoutEventsPaymentMethodTypes,
  PaddleEventData,
} from "@paddle/paddle-js";
import { SegmentedControl, SpinnerCenter } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { type PaddleBillingPrice, useInitialPeriod } from "../../hooks/useCheckout";
import { useEventListener } from "../../hooks/useEventListener";
import { usePaddleBilling } from "../../hooks/usePaddleBilling";
import { useVerifyPaddleBillingTransaction } from "../../hooks/usePaddleBillingCheckout";
import { usePriceConfig } from "../../hooks/usePriceConfig";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import {
  PaddleBillingAdapter,
  PaddleBillingCheckoutEventNames,
  PaddleBillingPaymentType,
} from "../../lib/PaddleBillingAdapter";
import { SubscriptionPeriod, SubscriptionTier } from "../../types";
import { formatCurrency } from "../../utils/currencyUtils";
import { measurePerformance } from "../../utils/performanceUtils";
import Heading from "../Heading/Heading";

import style from "./PaddleBillingCheckout.module.css";

interface CheckoutSummary {
  items: Array<{ name: string; subtotal: number }>;
  discount: number;
  tax: number;
  total: number;
  currencyCode: string;
}

interface PaddleBillingCheckoutProps {
  prices: PaddleBillingPrice[];
  customerId?: string;
  subscriptionTier?: SubscriptionTier | null;
}

const mapPaymentTypeToTracking = (
  paymentType: CheckoutEventsPaymentMethodTypes
): PaddleBillingPaymentType | undefined => {
  if (paymentType === "card") {
    return PaddleBillingPaymentType.Card;
  } else if (paymentType === "alipay") {
    return PaddleBillingPaymentType.Alipay;
  } else if (
    paymentType === ("wechat_pay" as CheckoutEventsPaymentMethodTypes) ||
    paymentType === "wechat-pay"
  ) {
    return PaddleBillingPaymentType.WechatPay;
  }
  return undefined;
};

const buildSummary = (data: CheckoutEventsData): CheckoutSummary => ({
  items: data.items.map((item) => ({
    name: item.product.name,
    subtotal: item.totals.subtotal,
  })),
  discount: data.totals.discount,
  tax: data.totals.tax,
  total: data.totals.total,
  currencyCode: data.currency_code,
});

export const PaddleBillingCheckout = ({
  prices,
  customerId,
  subscriptionTier,
}: PaddleBillingCheckoutProps) => {
  useLayoutEffect(() => {
    measurePerformance(
      "webapp:checkout:total-routing-time",
      "webapp:checkout:router-mounted",
      "webapp:checkout:checkout-mounted"
    );
  }, []);

  const { t, m } = useTranslation();
  const { data: accountData } = useAccount();
  const { isLoaded: isPaddleBillingLoaded, error: paddleBillingError } = usePaddleBilling();
  const {
    mutate: mutateVerifyPaddleBillingTransaction,
    isPending: isVerifyPaddleBillingTransactionPending,
  } = useVerifyPaddleBillingTransaction();
  const tracker = useTracker();
  const initialPeriod = useInitialPeriod();
  const navigation = useNavigation();
  const { data: priceConfigData } = usePriceConfig();

  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>(initialPeriod);
  const [isCheckoutReady, setIsCheckoutReady] = useState(false);
  const [checkoutSummary, setCheckoutSummary] = useState<CheckoutSummary | null>(null);
  const isCheckoutSuccess = useRef(false);
  const checkoutOpened = useRef(false);

  const selectedPrice = prices.find((p) => p.period === selectedPeriod);
  const selectedPriceId = selectedPrice?.priceId;

  useEventListener("beforeunload", tracker.trackCheckoutAbandonment.bind(tracker));
  useEffect(() => {
    return () => {
      if (!isCheckoutSuccess.current) {
        tracker.trackCheckoutAbandonment();
      }
    };
  }, [tracker]);

  const handleCheckoutComplete = useCallback(
    (transactionId: string, paymentType: CheckoutEventsPaymentMethodTypes) => {
      tracker.trackCheckoutFlowPaymentCompleted();
      mutateVerifyPaddleBillingTransaction(transactionId, {
        onSuccess: () => {
          tracker.trackCheckoutFlowCompleted(
            false,
            "paddle_billing",
            1,
            mapPaymentTypeToTracking(paymentType)
          );
          isCheckoutSuccess.current = true;
          navigation.goToNonRecurringPaymentSuccess();
        },
      });
    },
    [tracker, mutateVerifyPaddleBillingTransaction, navigation]
  );

  const handlePaymentFailed = useCallback(
    (_: PaddleEventData) => {
      tracker.trackCheckoutFlowFailed({
        trial: false,
        reason: "unknown",
        seats: 1,
        paymentPlatform: "paddle_billing",
      });
    },
    [tracker]
  );

  useEffect(() => {
    const adapter = PaddleBillingAdapter.getInstance();
    adapter.setEventCallback((event) => {
      if (
        event.name === PaddleBillingCheckoutEventNames.CHECKOUT_COMPLETED &&
        event.data?.transaction_id
      ) {
        const paymentType = event.data?.payment.method_details.type;
        handleCheckoutComplete(event.data.transaction_id, paymentType);
      }
      if (event.name === PaddleBillingCheckoutEventNames.CHECKOUT_PAYMENT_FAILED) {
        handlePaymentFailed(event);
      }
      if (event.name === PaddleBillingCheckoutEventNames.CHECKOUT_LOADED && event.data) {
        tracker.trackCheckoutFlowLoaded({
          paymentPlatform: "paddle_billing",
          trial: false,
          subscriptionTier,
        });
        setCheckoutSummary(buildSummary(event.data));
        setIsCheckoutReady(true);
      }
      if (
        (event.name === PaddleBillingCheckoutEventNames.CHECKOUT_UPDATED ||
          event.name === PaddleBillingCheckoutEventNames.CHECKOUT_ITEMS_UPDATED) &&
        event.data
      ) {
        setCheckoutSummary(buildSummary(event.data));
      }
    });
    return () => {
      adapter.setEventCallback(() => {});
    };
  }, [handleCheckoutComplete, handlePaymentFailed, subscriptionTier, tracker]);

  useEffect(() => {
    if (!isPaddleBillingLoaded || !selectedPriceId || !accountData?.id || checkoutOpened.current)
      return;

    PaddleBillingAdapter.getInstance().openInlineCheckout({
      customerId,
      priceId: selectedPriceId,
      containerSelector: style.paddleContainer,
      user: {
        userId: accountData.id,
        email: accountData?.email ?? "",
        countryCode: accountData?.countryCode ?? "",
      },
    });
    checkoutOpened.current = true;
  }, [
    customerId,
    isPaddleBillingLoaded,
    selectedPriceId,
    accountData?.id,
    accountData?.email,
    accountData?.countryCode,
  ]);

  const handlePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const period = e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly;
      setSelectedPeriod(period);

      const newPriceId = prices.find((p) => p.period === period)?.priceId;
      if (newPriceId) {
        PaddleBillingAdapter.getInstance().updateCheckoutItems([
          { priceId: newPriceId, quantity: 1 },
        ]);
      }
    },
    [prices]
  );

  useEffect(() => {
    if (paddleBillingError) {
      tracker.trackCheckoutFlowLoadError("unknown", "paddle_billing");
    }
  }, [paddleBillingError, tracker]);

  if (paddleBillingError) {
    return (
      <div className={style.wrapper}>
        <div className={classNames(style.pageWrapper, style.show)}>
          <div className={style.headerWrapper}>
            <Heading type="h2">{t(m.PaddleBillingCheckout.ErrorTitle)}</Heading>
            <div className={style.subtitle}>{t(m.PaddleBillingCheckout.ErrorDescription)}</div>
          </div>
        </div>
      </div>
    );
  }

  const isLoading = !isPaddleBillingLoaded || !isCheckoutReady;

  return (
    <div className={style.wrapper}>
      {(isLoading || isVerifyPaddleBillingTransactionPending) && (
        <div className={style.spinnerOverlay}>
          <SpinnerCenter />
        </div>
      )}
      <div
        className={classNames(
          style.pageWrapper,
          !isLoading && !isVerifyPaddleBillingTransactionPending && style.show
        )}
      >
        <div className={style.headerWrapper}>
          <Heading type="h2">{t(m.PaddleBillingCheckout.Title)}</Heading>
          <div className={style.subtitle}>{t(m.PaddleBillingCheckout.Subtitle)}</div>
        </div>
        <div className={style.inlineCheckoutWrapper}>
          <div className={style.summary}>
            <div className={style.summaryTitle}>{t(m.PaddleBillingCheckout.Summary)}</div>
            {checkoutSummary && (
              <>
                {checkoutSummary.items.map((item) => (
                  <div key={item.name} className={style.summaryRow}>
                    <span>{item.name}</span>
                    <span>{formatCurrency(item.subtotal, checkoutSummary.currencyCode)}</span>
                  </div>
                ))}
                {checkoutSummary.discount > 0 && (
                  <div className={style.summaryRow}>
                    <span>{t(m.PaddleBillingCheckout.LimitedOffer)}</span>
                    <span className={style.discountAmount}>
                      {formatCurrency(-checkoutSummary.discount, checkoutSummary.currencyCode)}
                    </span>
                  </div>
                )}
                {checkoutSummary.tax > 0 && (
                  <div className={style.summaryRow}>
                    <span>{t(m.PaddleBillingCheckout.Vat)}</span>
                    <span>{formatCurrency(checkoutSummary.tax, checkoutSummary.currencyCode)}</span>
                  </div>
                )}
                <div className={style.summaryDivider} />
                <div className={style.summaryRow}>
                  <span>{t(m.PaddleBillingCheckout.TotalDueToday)}</span>
                  <span>{formatCurrency(checkoutSummary.total, checkoutSummary.currencyCode)}</span>
                </div>
                {selectedPeriod === SubscriptionPeriod.Yearly && (
                  <div className={style.summaryNote}>
                    {t(m.PaddleBillingCheckout.AutoExpireYear)}
                  </div>
                )}
                {selectedPeriod === SubscriptionPeriod.Monthly && (
                  <div className={style.summaryNote}>
                    {t(m.PaddleBillingCheckout.AutoExpireMonth)}
                  </div>
                )}
              </>
            )}
          </div>
          <div className={style.paymentDetails}>
            <div className={style.subgroup}>
              <div className={style.checkoutLineText}>
                {t(m.PaddleBillingCheckout.BillingPeriod)}
              </div>
              <SegmentedControl
                className={style.periodPicker}
                checked={selectedPeriod === SubscriptionPeriod.Monthly}
                onChange={handlePeriodChange}
                optionA={t(m.PaddleBillingCheckout.OneYear)}
                optionB={t(m.PaddleBillingCheckout.OneMonth)}
                optionASecondaryText={
                  priceConfigData &&
                  `- ${t(m.PaddleBillingCheckout.SavePercent, {
                    savingsPercent: priceConfigData.tiers[SubscriptionTier.Lite]?.savingsPercent,
                  })}`
                }
              />
            </div>
            <div className={style.subgroup}>
              <div className={style.checkoutLineText}>
                {t(m.PaddleBillingCheckout.PaymentMethod)}
              </div>
              <div className={style.paddleContainer} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
